import type { ZodType } from "zod";

import {
  CaseDocumentsArchiveResponseSchema,
  type DocumentType,
  DocumentViewUrlResponseSchema,
  type GenerateUploadUrlRequest,
  type GenerateUploadUrlResponse,
  GenerateUploadUrlResponseSchema,
  type GenerateUploadUrlsRequest,
  type GenerateUploadUrlsResponse,
  GenerateUploadUrlsResponseSchema,
  ListDocumentApiResponsesSchema,
} from "@recobra/schemas/document";

import { HttpError, requestJson, requestVoid } from "./client";

export class DuplicateDocumentUploadError extends Error {
  readonly duplicates: DuplicateDocumentUpload[];

  constructor(duplicates: DuplicateDocumentUpload[]) {
    super("duplicate document upload");
    this.name = "DuplicateDocumentUploadError";
    this.duplicates = duplicates;
  }
}

export type DuplicateDocumentUpload = {
  filename?: string;
  fileFingerprint: string;
  existingDocumentId?: string;
  existingFilename?: string;
};

export const documentsApi = {
  requestUploadUrl: (body: GenerateUploadUrlRequest) =>
    requestDocumentUpload<GenerateUploadUrlResponse>(body, GenerateUploadUrlResponseSchema),

  requestUploadUrls: (body: GenerateUploadUrlsRequest) =>
    requestDocumentUpload<GenerateUploadUrlsResponse>(body, GenerateUploadUrlsResponseSchema),

  listDocuments: (caseId: string) =>
    requestJson(`/cases/${caseId}/documents`, ListDocumentApiResponsesSchema),

  buildCaseDocumentsArchive: (caseId: string) =>
    requestJson(`/cases/${caseId}/documents/download`, CaseDocumentsArchiveResponseSchema),

  listClientDocuments: (clientId: string) =>
    requestJson(`/clients/${clientId}/documents`, ListDocumentApiResponsesSchema),

  getViewUrl: (id: string) =>
    requestJson(`/documents/${id}/view-url`, DocumentViewUrlResponseSchema),

  deleteDocument: (id: string) => requestVoid(`/documents/${id}`, { method: "DELETE" }),

  overrideClassification: (id: string, documentType: DocumentType) =>
    requestVoid(`/documents/${id}/classification`, {
      method: "PATCH",
      body: JSON.stringify({ documentType }),
    }),
};

const requestDocumentUpload = async <T>(
  body: GenerateUploadUrlRequest | GenerateUploadUrlsRequest,
  schema: ZodType<T>,
): Promise<T> => {
  try {
    return await requestJson("/documents/upload-url", schema, {
      method: "POST",
      body: JSON.stringify(body),
    });
  } catch (err) {
    const duplicates = getDuplicateDocumentUploads(err);
    if (duplicates) throw new DuplicateDocumentUploadError(duplicates);

    throw err;
  }
};

const getDuplicateDocumentUploads = (err: unknown): DuplicateDocumentUpload[] | undefined => {
  if (!(err instanceof HttpError) || err.status !== 409) return undefined;
  const data = err.data;
  if (!isRecord(data)) return undefined;

  const details = data.details;
  if (!isRecord(details)) return undefined;

  const rawDuplicates = details.duplicates;
  if (!Array.isArray(rawDuplicates)) return undefined;

  const duplicates = rawDuplicates.flatMap((item): DuplicateDocumentUpload[] => {
    if (!isRecord(item) || typeof item.fileFingerprint !== "string") return [];

    return [
      {
        fileFingerprint: item.fileFingerprint,
        filename: typeof item.filename === "string" ? item.filename : undefined,
        existingDocumentId:
          typeof item.existingDocumentId === "string" ? item.existingDocumentId : undefined,
        existingFilename:
          typeof item.existingFilename === "string" ? item.existingFilename : undefined,
      },
    ];
  });

  return duplicates.length > 0 ? duplicates : undefined;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * PUT a file directly to S3 via presigned URL.
 * Uses XHR instead of fetch — fetch has no upload.onprogress event.
 */
export const uploadToS3 = (
  presignedUrl: string,
  file: File,
  mimeType: string,
  onProgress: (pct: number) => void,
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed: ${xhr.status}`));
      }
    });
    xhr.addEventListener("error", () => reject(new Error("Network error during upload")));
    xhr.open("PUT", presignedUrl);
    xhr.setRequestHeader("Content-Type", mimeType);
    xhr.send(file);
  });
